import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import acceptJoinRequestService from '../../services/joinRequests/acceptJoinRequestService';
import { mapPrincipalCommunityApps } from '../../helpers/principals/mapPrincipalCommunityApps';
import joinRequestService from '../../services/joinRequests/joinRequestService';
import PrincipalForm from '../principals/PrincipalForm';

const AcceptJoinRequest = ({
  open,
  organisationId,
  rootUrl,
  acceptJoinRequestFetch,
  dispatchAcceptJoinRequestPost,
  dispatchJoinRequestGet,
  joinRequestFetch,
}) => {
  const { t } = useTranslation();
  const { actionId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (open && actionId) {
      dispatchJoinRequestGet(organisationId, actionId, () => navigate(rootUrl), t);
    }
  }, [t, open, organisationId, actionId, dispatchJoinRequestGet, rootUrl, navigate]);

  const handleCreateCommunityMember = useCallback(
    body => {
      dispatchAcceptJoinRequestPost(
        organisationId,
        actionId,
        {
          principal: {
            ...body,
            inviteRequest: {
              ...body.inviteRequest,
              sendInvitation: true,
              invitationType: 1, // temporary fixed (1 email), will later come from api
            },
            organisation: { id: organisationId },
            communityApps: mapPrincipalCommunityApps(body.communityApps),
          },
          principalId: body.principalId,
        },
        () => navigate(`${rootUrl}/principals`),
        undefined,
        t
      );
    },
    [rootUrl, actionId, organisationId, dispatchAcceptJoinRequestPost, navigate, t]
  );

  const defaultData = useMemo(() => {
    if (!joinRequestFetch.value) return null;

    return {
      fullName: joinRequestFetch.value?.principal?.fullName,
      primaryEmailAddress: joinRequestFetch.value?.principal?.primaryEmailAddress,
      primaryPhoneNumber: joinRequestFetch.value?.principal?.primaryPhoneNumber,
      languageId: joinRequestFetch.value?.principal?.languageId,
      timeZone: joinRequestFetch.value?.principal?.timeZone,
      communityApps: (joinRequestFetch.value?.principal?.communityApps || []).map(ca => ({
        app: `${ca.appInstallation?.tag}_${ca.community?.tag}`,
        features: (ca.communityAppFeatures || []).map(caf => ({
          feature: caf.appInstallationFeature?.tag,
          roles: (caf.communityAppFeatureRoles || []).map(r => r.appInstallationFeatureRole?.tag),
        })),
      })),
    };
  }, [joinRequestFetch.value]);

  if (!open) return null;

  return (
    <PrincipalForm
      header={t('acceptJoinRequestFor', { user: joinRequestFetch.value?.principal?.fullName })}
      onSubmit={handleCreateCommunityMember}
      submitProcessing={acceptJoinRequestFetch.pending}
      returnRoute={`${rootUrl}/principals`}
      cancelRoute={`${rootUrl}/principals`}
      data={defaultData}
      organisationId={organisationId}
      showPrincipalIdField
    />
  );
};

AcceptJoinRequest.propTypes = {
  dispatchAcceptJoinRequestPost: PropTypes.func.isRequired,
  acceptJoinRequestFetch: PropTypes.object.isRequired,
  dispatchJoinRequestGet: PropTypes.func.isRequired,
  joinRequestFetch: PropTypes.object.isRequired,
  organisationId: PropTypes.string.isRequired,
  rootUrl: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
};

export default compose(joinRequestService, acceptJoinRequestService)(AcceptJoinRequest);
